import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BuildIcon from "@mui/icons-material/Build";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../api";
import StarRating from "../../Admin/components/StarRating";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const FORMAS_PAGO = [
  { key: "CONTADO", label: "Contado" },
  { key: "FINANCIACION", label: "Financiación" },
  { key: "ENTREGA", label: "Entrega" },
];

const normalizeApiMessage = (message = "") => {
  const text = String(message || "");

  if (
    text.toLowerCase().includes("email address") ||
    text.toLowerCase().includes("valid email") ||
    text.toLowerCase().includes("value is not a valid email")
  ) {
    return "Ingresá un email válido o dejá el campo vacío.";
  }

  if (text.toLowerCase().includes("field required")) {
    return "Falta completar un dato obligatorio.";
  }

  return text;
};

const getApiErrorMessage = (data, fallback = "No se pudo completar la operación.") => {
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (!detail) return fallback;

  if (typeof detail === "string") {
    return normalizeApiMessage(detail);
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return normalizeApiMessage(item);
        if (item?.msg) return normalizeApiMessage(item.msg);
        if (item?.message) return normalizeApiMessage(item.message);

        if (item?.loc && item?.msg) {
          return normalizeApiMessage(item.msg);
        }

        return null;
      })
      .filter(Boolean)
      .join(" | ") || fallback;
  }

  if (typeof detail === "object") {
    if (detail.msg) return normalizeApiMessage(detail.msg);
    if (detail.message) return normalizeApiMessage(detail.message);
    return fallback;
  }

  return fallback;
};

const toNumberOrNull = (value) => {
  // strips visual thousand-separator dots before parsing
  const raw = String(value ?? "").trim().replace(/\./g, "").replace(/,/g, ".");
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

// Formats digits with "." every 3: "1234567" → "1.234.567"
const formatThousands = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const notifyVentaRegistrada = (idVenCli) => {
  const payload = {
    type: "venta-registrada",
    id_ven_cli: Number(idVenCli),
    at: Date.now(),
  };
  try {
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      const channel = new BroadcastChannel("ventas-channel");
      channel.postMessage(payload);
      channel.close();
    }
  } catch (e) {}
  try {
    localStorage.setItem("venta_registrada_event", JSON.stringify(payload));
  } catch (e) {}
};

const getPrefill = (idVenCli) => {
  try {
    const raw = localStorage.getItem(`venta_prefill_${idVenCli}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// ─── Toggle visual tipo "pill" para NUEVO / USADO ────────────────────────────
function VehicleStateToggle({ value, onChange }) {
  const isUsado = value === "USADO";
  return (
    <Box
      sx={{
        display: "inline-flex",
        borderRadius: "999px",
        border: "1.5px solid",
        borderColor: isUsado ? "warning.main" : "primary.main",
        overflow: "hidden",
        transition: "border-color 0.25s",
        alignSelf: "flex-start",
      }}
    >
      {[
        { label: "Nuevo", val: "NUEVO", icon: <DirectionsCarIcon sx={{ fontSize: 15, mr: 0.5 }} /> },
        { label: "Usado", val: "USADO", icon: <BuildIcon sx={{ fontSize: 15, mr: 0.5 }} /> },
      ].map(({ label, val, icon }) => {
        const active = value === val;
        return (
          <Box
            key={val}
            onClick={() => onChange(val)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 0.75,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.82rem",
              letterSpacing: 0.3,
              userSelect: "none",
              transition: "all 0.2s",
              bgcolor: active
                ? val === "NUEVO"
                  ? "primary.main"
                  : "warning.main"
                : "transparent",
              color: active ? "#fff" : "text.secondary",
              "&:hover": {
                bgcolor: active
                  ? val === "NUEVO"
                    ? "primary.dark"
                    : "warning.dark"
                  : val === "NUEVO"
                    ? "primary.50"
                    : "warning.50",
              },
            }}
          >
            {icon}
            {label}
          </Box>
        );
      })}
    </Box>
  );
}

const isValidEmail = (email) => {
  const value = String(email || "").trim();
  if (!value) return true;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

// ─────────────────────────────────────────────────────────────────────────────

export default function RegistrarVentaPage() {
  const feedbackRef = useRef(null);
  const navigate = useNavigate();
  const { idVenCli } = useParams();

  const parsedIdVenCli = useMemo(() => Number(idVenCli), [idVenCli]);
  const invalidId = !Number.isFinite(parsedIdVenCli) || parsedIdVenCli <= 0;

  // Default 1 estrella (= 2/10) en lugar de 2.5
  const [ratingStars, setRatingStars] = useState(1);

  const [form, setForm] = useState({
    estado_vehiculo: "NUEVO",
    marca: "",
    modelo: "",
    version: "",
    color: "",
    kilometraje: "",
    precio_venta: "",
    moneda_venta: "ARS",
    forma_pago: "CONTADO",
    formas_pago: ["CONTADO"],
    monto_efectivo: "",
    moneda_pago: "ARS",
    moneda_contado: "ARS",
    tipo_cambio_contado: "",
    monto_financiado: "",
    tipo_financiacion: "",
    financiacion_cuotas: "",
    cuota_aproximada: "",
    moneda_financiacion: "ARS",
    tipo_cambio_financiacion: "",
    entrega_usado: false,
    usado_marca: "",
    usado_modelo_version: "",
    usado_anio: "",
    usado_kilometraje: "",
    usado_valor_toma: "",
  });

  // Cliente con todos los campos del schema
  const [cliente, setCliente] = useState({
    id: null,
    nombre: "",
    telefono: "",
    email: "",
    obtenido_de: "",
    // Nuevos campos
    fecha_nacimiento: "",
    direccion: "",
    localidad: "",
    codigo_postal: "",
    dni: "",
    cuit_cuil: "",
    estado_civil: "",
    // Cónyuge
    conyuge_nombre: "",
    conyuge_dni: "",
    conyuge_fecha_nacimiento: "",
  });

  const [loadingCliente, setLoadingCliente] = useState(false);
  const [vehiculoConsultadoVenta, setVehiculoConsultadoVenta] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [clienteError, setClienteError] = useState("");
  const [success, setSuccess] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState(null);

  const score10 = useMemo(
    () => clamp(Math.round(Number(ratingStars || 0) * 2), 1, 10),
    [ratingStars]
  );

  useEffect(() => {
    if (invalidId) return;
    const prefill = getPrefill(parsedIdVenCli);
    if (!prefill?.id_cliente) return;
    setVehiculoConsultadoVenta(prefill?.vehiculo_consultado ?? prefill?.consulta ?? "");

    setLoadingCliente(true);
    setClienteError("");

    apiFetch(`/api/clientes/${prefill.id_cliente}`, { credentials: "include" })
      .then(async (res) => {
        if (!res?.ok) throw new Error(`HTTP ${res?.status ?? "?"}`);
        const data = await res.json();
        setCliente({
          id: data?.id ?? prefill.id_cliente,
          nombre: data?.nombre ?? prefill?.cliente_nombre ?? "",
          telefono: data?.telefono ?? "",
          email: data?.email ?? "",
          obtenido_de: data?.obtenido_de ?? "",
          fecha_nacimiento: data?.fecha_nacimiento ?? "",
          direccion: data?.direccion ?? "",
          localidad: data?.localidad ?? "",
          codigo_postal: data?.codigo_postal ?? "",
          dni: data?.dni ?? "",
          cuit_cuil: data?.cuit_cuil ?? "",
          estado_civil: data?.estado_civil ?? "",
          conyuge_nombre: data?.conyuge_nombre ?? "",
          conyuge_dni: data?.conyuge_dni ?? "",
          conyuge_fecha_nacimiento: data?.conyuge_fecha_nacimiento ?? "",
        });
      })
      .catch(() => {
        setCliente((prev) => ({
          ...prev,
          id: prefill.id_cliente,
          nombre: prefill?.cliente_nombre ?? "",
        }));
        setClienteError(
          "No se pudieron cargar todos los datos del cliente. Podés completar y guardar igual."
        );
      })
      .finally(() => setLoadingCliente(false));
  }, [parsedIdVenCli, invalidId]);

  const setField = (field) => (event) => {
    const value =
      event?.target?.type === "checkbox"
        ? event.target.checked
        : event?.target?.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setClienteField = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setCliente((prev) => ({ ...prev, [field]: value }));
  };

  // For money fields: strips non-digits, auto-formats with "." every 3 digits
  const setMoneyField = (field) => (event) => {
    const formatted = formatThousands(event?.target?.value ?? "");
    setForm((prev) => ({ ...prev, [field]: formatted }));
  };

  const setDecimalField = (field) => (event) => {
    const value = String(event?.target?.value ?? "").replace(/[^0-9.,]/g, "");
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleFormaPago = (key) => {
    setForm((prev) => {
      const current = Array.isArray(prev.formas_pago) && prev.formas_pago.length
        ? prev.formas_pago
        : ["CONTADO"];
      const exists = current.includes(key);
      if (exists && current.length === 1) return prev;
      const next = exists ? current.filter((item) => item !== key) : [...current, key];
      return {
        ...prev,
        formas_pago: next,
        entrega_usado: next.includes("ENTREGA"),
      };
    });
  };

  const hasFormaPago = (key) => Array.isArray(form.formas_pago) && form.formas_pago.includes(key);

  const closeVentaTab = useCallback(() => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
      }
    } catch (e) {
      // noop
    }

    try {
      window.close();
    } catch (e) {
      // noop
    }

    setTimeout(() => {
      if (window.closed) return;
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/ventas/tablero", { replace: true });
      }
    }, 100);
  }, [navigate]);

  useEffect(() => {
    if (!success || redirectSeconds === null) return undefined;
    if (redirectSeconds <= 0) {
      closeVentaTab();
      return undefined;
    }
    const timer = setTimeout(() => {
      setRedirectSeconds((prev) => (prev === null ? null : prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [success, redirectSeconds, closeVentaTab]);

  useEffect(() => {
    if (!success) return;

    requestAnimationFrame(() => {
      if (feedbackRef.current) {
        feedbackRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
  }, [success]);

  const validateCommercialFields = () => {
    const precioVenta = toNumberOrNull(form.precio_venta);
    const formasPago = Array.isArray(form.formas_pago) ? form.formas_pago : [];
    const montoContado = toNumberOrNull(form.monto_efectivo);
    const montoFinanciado = toNumberOrNull(form.monto_financiado);
    const cuotaAproximada = toNumberOrNull(form.cuota_aproximada);
    const financiacionCuotas = toNumberOrNull(form.financiacion_cuotas);
    const tipoCambioContado = toNumberOrNull(form.tipo_cambio_contado);
    const tipoCambioFinanciacion = toNumberOrNull(form.tipo_cambio_financiacion);
    const valorToma = toNumberOrNull(form.usado_valor_toma);

    if (!isValidEmail(cliente.email)) {
      return "Ingresá un email válido o dejá el campo vacío.";
    }

    if (!form.marca?.trim()) return "Completá la marca del vehículo.";
    if (!form.modelo?.trim()) return "Completá el modelo del vehículo.";
    if (!precioVenta || precioVenta <= 0) return "Completá un precio de venta válido.";
    if (!form.moneda_venta) return "Seleccioná la moneda de venta.";
    if (!formasPago.length) return "Seleccioná al menos una forma de pago.";

    if (formasPago.includes("CONTADO")) {
      if (!montoContado || montoContado <= 0) return "Completá el monto de contado.";
      if (!form.moneda_contado) return "Seleccioná la moneda de contado.";
      if (form.moneda_contado === "USD" && (!tipoCambioContado || tipoCambioContado <= 0)) {
        return "Completá el tipo de cambio de contado.";
      }
    }

    if (formasPago.includes("FINANCIACION")) {
      if (!montoFinanciado || montoFinanciado <= 0) return "Completá el monto financiado.";
      if (!form.tipo_financiacion?.trim()) return "Completá el tipo de financiación.";
      if (!financiacionCuotas || financiacionCuotas <= 0) return "Completá el número de cuotas.";
      if (!cuotaAproximada || cuotaAproximada <= 0) return "Completá el valor aproximado de cuota.";
      if (!form.moneda_financiacion) return "Seleccioná la moneda de financiación.";
      if (form.moneda_financiacion === "USD" && (!tipoCambioFinanciacion || tipoCambioFinanciacion <= 0)) {
        return "Completá el tipo de cambio de financiación.";
      }
    }

    if (formasPago.includes("ENTREGA") && (!valorToma || valorToma <= 0)) {
      return "Completá el valor de toma del usado.";
    }

    return "";
  };

  // ── Único botón "Guardar venta": backend atómico ─
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (invalidId || submitting) return;

    setSubmitting(true);
    setError("");

    try {
      const validationError = validateCommercialFields();
      if (validationError) throw new Error(validationError);

      const clientePayload = cliente?.id ? {
        nombre: cliente.nombre || "",
        telefono: cliente.telefono || "",
        email: cliente.email?.trim() ? cliente.email.trim() : null,
        obtenido_de: cliente.obtenido_de || null,
        fecha_nacimiento: cliente.fecha_nacimiento || null,
        direccion: cliente.direccion || null,
        localidad: cliente.localidad || null,
        codigo_postal: cliente.codigo_postal || null,
        dni: cliente.dni || null,
        cuit_cuil: cliente.cuit_cuil || null,
        estado_civil: cliente.estado_civil || null,
        conyuge_nombre: cliente.conyuge_nombre || null,
        conyuge_dni: cliente.conyuge_dni || null,
        conyuge_fecha_nacimiento: cliente.conyuge_fecha_nacimiento || null,
      } : null;

      const formasPago = Array.isArray(form.formas_pago) && form.formas_pago.length
        ? form.formas_pago
        : ["CONTADO"];
      const legacyFormaPago = formasPago.length === 1
        ? (formasPago[0] === "FINANCIACION" ? "FINANCIADO" : formasPago[0])
        : "MIXTO";
      const incluyeEntrega = formasPago.includes("ENTREGA");

      const ventaPayload = {
        id_ven_cli: parsedIdVenCli,
        estado_vehiculo: form.estado_vehiculo || null,
        marca: form.marca || null,
        modelo: form.modelo || null,
        version: form.version || null,
        color: form.color || null,
        kilometraje: form.kilometraje || null,
        precio_venta: toNumberOrNull(form.precio_venta),
        moneda_venta: form.moneda_venta || null,
        forma_pago: legacyFormaPago,
        formas_pago: formasPago,
        monto_efectivo: formasPago.includes("CONTADO") ? toNumberOrNull(form.monto_efectivo) : null,
        moneda_pago: formasPago.includes("CONTADO") ? form.moneda_contado || null : null,
        moneda_contado: formasPago.includes("CONTADO") ? form.moneda_contado || null : null,
        tipo_cambio_contado: formasPago.includes("CONTADO") && form.moneda_contado === "USD" ? toNumberOrNull(form.tipo_cambio_contado) : null,
        monto_financiado: formasPago.includes("FINANCIACION") ? toNumberOrNull(form.monto_financiado) : null,
        tipo_financiacion: formasPago.includes("FINANCIACION") ? form.tipo_financiacion || null : null,
        financiacion_cuotas: formasPago.includes("FINANCIACION") ? toNumberOrNull(form.financiacion_cuotas) : null,
        cuota_aproximada: formasPago.includes("FINANCIACION") ? toNumberOrNull(form.cuota_aproximada) : null,
        moneda_financiacion: formasPago.includes("FINANCIACION") ? form.moneda_financiacion || null : null,
        tipo_cambio_financiacion: formasPago.includes("FINANCIACION") && form.moneda_financiacion === "USD" ? toNumberOrNull(form.tipo_cambio_financiacion) : null,
        entrega_usado: incluyeEntrega,
        usado_marca: incluyeEntrega ? form.usado_marca || null : null,
        usado_modelo_version: incluyeEntrega ? form.usado_modelo_version || null : null,
        usado_anio: incluyeEntrega ? form.usado_anio || null : null,
        usado_kilometraje: incluyeEntrega ? form.usado_kilometraje || null : null,
        usado_valor_toma: incluyeEntrega ? toNumberOrNull(form.usado_valor_toma) : null,
      };
      const resVenta = await apiFetch("/api/ventas/registrar-cierre", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          cliente_id: cliente?.id ?? null,
          cliente: clientePayload,
          venta: ventaPayload,
          puntuacion_venta: score10,
        }),
      });
      if (!resVenta.ok) {
        const data = await resVenta.json().catch(() => ({}));
        throw new Error(
          getApiErrorMessage(data, `No se pudo registrar la venta. HTTP ${resVenta.status}`)
        );
      }

      setSuccess(true);
      setRedirectSeconds(10);
      notifyVentaRegistrada(parsedIdVenCli);
    } catch (e) {
      setError(e?.message || "No se pudo registrar la venta.");

      requestAnimationFrame(() => {
        feedbackRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (invalidId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">ID de asignación inválido.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1080, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "rgba(15, 23, 42, 0.1)",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.92) 100%)",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Stack spacing={2.5}>
            {/* Header */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={2}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                  Registro de venta
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  Asignación #{parsedIdVenCli}. Completá los datos del cliente y de la operación.
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "rgba(15, 23, 42, 0.12)",
                  minWidth: { xs: "100%", md: 320 },
                  background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ textTransform: "uppercase", letterSpacing: 1, color: "text.disabled", fontWeight: 700 }}
                >
                  Puntuación del cliente
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
                  Calificá la calidad o valor comercial de esta oportunidad.
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 0.75, pointerEvents: success || submitting ? "none" : "auto", opacity: success || submitting ? 0.7 : 1 }}
                >
                  <StarRating
                    value={ratingStars}
                    onChange={setRatingStars}
                    precision={0.5}
                    size={24}
                    aria-label="Puntuacion del cliente"
                  />
                  <Typography sx={{ fontWeight: 700 }}>{score10}/10</Typography>
                </Stack>
              </Paper>
            </Stack>

            <Box ref={feedbackRef} />

            {clienteError && <Alert severity="warning">{clienteError}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {success && (
              <Alert severity="success">
                Venta registrada correctamente. La tabla del vendedor se actualizó en la otra pestaña.
                {redirectSeconds !== null && ` Esta pestaña se cerrará en ${redirectSeconds} segundos.`}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={1.5}>

                <Box
                  component="fieldset"
                  disabled={success || submitting}
                  sx={{
                    border: "none", p: 0, m: 0, minWidth: 0,
                    pointerEvents: success || submitting ? "none" : "auto",
                    opacity: success || submitting ? 0.8 : 1
                  }}
                >
                  <Stack spacing={1.5}>

                {/* ── ACCORDION CLIENTE ── */}
                <Accordion
                  defaultExpanded
                  sx={{
                    borderRadius: "16px !important",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "rgba(148, 163, 184, 0.25)",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(241, 245, 249, 0.8)",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                      minHeight: 52,
                      "& .MuiAccordionSummary-content": { my: 1.5 },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                      Cliente
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: { xs: 2, md: 3 } }}>
                    {loadingCliente ? (
                      <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Stack spacing={2.5}>
                        {/* Datos básicos */}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", mb: 1, display: "block" }}>
                            Datos de contacto
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField fullWidth label="Nombre" value={cliente.nombre} onChange={setClienteField("nombre")} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField fullWidth label="Teléfono" value={cliente.telefono} onChange={setClienteField("telefono")} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField fullWidth label="Email" type="email" value={cliente.email} onChange={setClienteField("email")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth label="Obtenido de" value={cliente.obtenido_de} onChange={setClienteField("obtenido_de")} />
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Datos personales */}
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", mb: 1, display: "block" }}>
                            Datos personales
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField fullWidth label="DNI" value={cliente.dni} onChange={setClienteField("dni")} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField fullWidth label="CUIT / CUIL" value={cliente.cuit_cuil} onChange={setClienteField("cuit_cuil")} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                label="Fecha de nacimiento"
                                type="date"
                                value={cliente.fecha_nacimiento}
                                onChange={setClienteField("fecha_nacimiento")}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            {/* Estado civil — último campo, mismo ancho que los demás */}
                            <Grid item xs={12} sm={6} md={3}>
                              <FormControl fullWidth>
                                <InputLabel id="estado-civil-label">Estado civil</InputLabel>
                                <Select
                                  labelId="estado-civil-label"
                                  label="Estado civil"
                                  value={cliente.estado_civil || ""}
                                  onChange={setClienteField("estado_civil")}
                                  sx={{ minWidth: 150 }}
                                >
                                  <MenuItem value="Soltero/a">Soltero/a</MenuItem>
                                  <MenuItem value="Casado/a">Casado/a</MenuItem>
                                  <MenuItem value="Divorciado/a">Divorciado/a</MenuItem>
                                  <MenuItem value="Viudo/a">Viudo/a</MenuItem>
                                  <MenuItem value="Unión convivencial">Unión convivencial</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={10}>
                              <TextField fullWidth label="Dirección" value={cliente.direccion} onChange={setClienteField("direccion")} />
                            </Grid>
                            <Grid item xs={12} sm={8} md={4}>
                              <TextField fullWidth label="Localidad" value={cliente.localidad} onChange={setClienteField("localidad")} />
                            </Grid>
                            <Grid item xs={12} sm={4} md={2}>
                              <TextField fullWidth label="Cód. postal" value={cliente.codigo_postal} onChange={setClienteField("codigo_postal")} />
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Datos cónyuge — sección separada, visible solo cuando aplica */}
                        {(cliente.estado_civil === "Casado/a" || cliente.estado_civil === "Unión convivencial") && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", mb: 1, display: "block" }}>
                              Datos del cónyuge
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={5}>
                                <TextField fullWidth label="Nombre" value={cliente.conyuge_nombre} onChange={setClienteField("conyuge_nombre")} />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <TextField fullWidth label="DNI" value={cliente.conyuge_dni} onChange={setClienteField("conyuge_dni")} />
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                  fullWidth
                                  label="Fecha de nacimiento"
                                  type="date"
                                  value={cliente.conyuge_fecha_nacimiento}
                                  onChange={setClienteField("conyuge_fecha_nacimiento")}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* ── ACCORDION VEHÍCULO ── */}
                <Accordion
                  defaultExpanded
                  sx={{
                    borderRadius: "16px !important",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "rgba(148, 163, 184, 0.25)",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(241, 245, 249, 0.8)",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                      minHeight: 52,
                      "& .MuiAccordionSummary-content": { my: 1.5 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                        Vehículo
                      </Typography>
                      {vehiculoConsultadoVenta && (
                        <Chip
                          size="small"
                          label={vehiculoConsultadoVenta}
                          sx={{
                            height: 24,
                            maxWidth: { xs: 180, sm: 320 },
                            fontSize: 11,
                            fontWeight: 800,
                            color: "#0059f6",
                            bgcolor: "rgba(0, 89, 246, 0.10)",
                            border: "1px solid rgba(0, 89, 246, 0.18)",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      {/* Toggle mejorado Nuevo / Usado */}
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", mb: 1, display: "block" }}>
                          Estado del vehículo
                        </Typography>
                        <VehicleStateToggle
                          value={form.estado_vehiculo}
                          onChange={(val) =>
                            setForm((prev) => ({ ...prev, estado_vehiculo: val }))
                          }
                        />
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField 
                            fullWidth 
                            required 
                            label="Marca" 
                            value={form.marca} 
                            onChange={setField("marca")} 
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField 
                            fullWidth 
                            required 
                            label="Modelo" 
                            value={form.modelo} 
                            onChange={setField("modelo")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="Versión" value={form.version} onChange={setField("version")} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="Color" value={form.color} onChange={setField("color")} />
                        </Grid>
                        {form.estado_vehiculo === "USADO" && (
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Kilometraje" value={form.kilometraje} onChange={setMoneyField("kilometraje")} inputProps={{ inputMode: "numeric" }} />
                          </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                          <TextField 
                            fullWidth 
                            required 
                            label="Precio venta" 
                            value={form.precio_venta} 
                            onChange={setMoneyField("precio_venta")} 
                            inputProps={{ inputMode: "numeric" }} 
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                        <FormControl fullWidth required>
                          <InputLabel id="moneda-venta-label">Moneda venta</InputLabel>
                          <Select 
                            labelId="moneda-venta-label" 
                            label="Moneda venta" 
                            sx={{ minWidth: 100 }} 
                            value={form.moneda_venta} 
                            onChange={setField("moneda_venta")}
                          >
                            <MenuItem value="ARS">ARS</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      </Grid>
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* ── ACCORDION DATOS DE OPERACIÓN ── */}
                <Accordion
                  defaultExpanded
                  sx={{
                    borderRadius: "16px !important",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "rgba(148, 163, 184, 0.25)",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "rgba(241, 245, 249, 0.8)",
                      borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
                      minHeight: 52,
                      "& .MuiAccordionSummary-content": { my: 1.5 },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
                      Datos de operación
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2.5}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                        {FORMAS_PAGO.map((option) => {
                          const active = hasFormaPago(option.key);
                          return (
                            <Button
                              key={option.key}
                              type="button"
                              variant={active ? "contained" : "outlined"}
                              onClick={() => toggleFormaPago(option.key)}
                              sx={{
                                minWidth: { xs: "100%", sm: 150 },
                                borderRadius: "8px",
                                textTransform: "none",
                                fontWeight: 800,
                              }}
                            >
                              {option.label}
                            </Button>
                          );
                        })}
                      </Stack>

                      {hasFormaPago("CONTADO") && (
                        <Box sx={{ border: "1px solid rgba(0,89,246,0.16)", bgcolor: "rgba(0,89,246,0.03)", borderRadius: 2, p: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                            Detalle de operación de contado
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Monto" value={form.monto_efectivo} onChange={setMoneyField("monto_efectivo")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth required>
                                <InputLabel>Moneda</InputLabel>
                                <Select label="Moneda" value={form.moneda_contado} onChange={setField("moneda_contado")}>
                                  <MenuItem value="ARS">Pesos</MenuItem>
                                  <MenuItem value="USD">USD</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {form.moneda_contado === "USD" && (
                              <Grid item xs={12} md={4}>
                                <TextField fullWidth required label="Tipo de cambio" value={form.tipo_cambio_contado} onChange={setDecimalField("tipo_cambio_contado")} inputProps={{ inputMode: "decimal" }} />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}

                      {hasFormaPago("FINANCIACION") && (
                        <Box sx={{ border: "1px solid rgba(25,118,210,0.16)", bgcolor: "rgba(25,118,210,0.03)", borderRadius: 2, p: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                            Detalle de financiación
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Monto" value={form.monto_financiado} onChange={setMoneyField("monto_financiado")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Tipo de financiación" value={form.tipo_financiacion} onChange={setField("tipo_financiacion")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Número de cuotas" value={form.financiacion_cuotas} onChange={setMoneyField("financiacion_cuotas")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Valor aprox. cuota" value={form.cuota_aproximada} onChange={setMoneyField("cuota_aproximada")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth required>
                                <InputLabel>Moneda</InputLabel>
                                <Select label="Moneda" value={form.moneda_financiacion} onChange={setField("moneda_financiacion")}>
                                  <MenuItem value="ARS">Pesos</MenuItem>
                                  <MenuItem value="USD">USD</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {form.moneda_financiacion === "USD" && (
                              <Grid item xs={12} md={4}>
                                <TextField fullWidth required label="Tipo de cambio" value={form.tipo_cambio_financiacion} onChange={setDecimalField("tipo_cambio_financiacion")} inputProps={{ inputMode: "decimal" }} />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      )}

                      {hasFormaPago("ENTREGA") && (
                        <Box sx={{ border: "1px solid rgba(76,175,80,0.22)", bgcolor: "rgba(76,175,80,0.04)", borderRadius: 2, p: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6, mb: 1.5, display: "block" }}>
                            Detalle de entrega
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth label="Marca usado" value={form.usado_marca} onChange={setField("usado_marca")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth label="Modelo y versión usado" value={form.usado_modelo_version} onChange={setField("usado_modelo_version")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth label="Año usado" value={form.usado_anio} onChange={setField("usado_anio")} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth label="Kilometraje usado" value={form.usado_kilometraje} onChange={setMoneyField("usado_kilometraje")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField fullWidth required label="Valor toma usado" value={form.usado_valor_toma} onChange={setMoneyField("usado_valor_toma")} inputProps={{ inputMode: "numeric" }} />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
                  </Stack>
                </Box>

                {/* ── BOTONES ── */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 2, borderTop: "1px solid", borderColor: "rgba(148, 163, 184, 0.2)" }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={closeVentaTab}
                    disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 600, px: 3 }}
                  >
                    Volver al tablero
                  </Button>

                  {/* Único botón de guardar */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={submitting ? null : <SaveIcon />}
                    disabled={submitting || success}
                    sx={{ minWidth: 200, borderRadius: 2, fontWeight: 700, letterSpacing: 0.3, boxShadow: "0 4px 14px rgba(25, 118, 210, 0.3)" }}
                  >
                    {submitting ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} color="inherit" />
                        Guardando...
                      </Box>
                    ) : (
                      "Guardar venta"
                    )}
                  </Button>
                </Stack>

              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
